import { useCallback, useEffect, useId, useState } from "react";
import { api, isNetworkError } from "../api";
import { ConfirmDialog, DocsLink, ErrorBanner, Skeleton, useEscape } from "../components";
import { useDropdown } from "../hooks";
import { PROVIDERS, SEVERITIES, deliveryStatus, destinationSummary, pauseSummary, severityInfo, validateDestination, verificationCode, withReceives } from "../alerts";

// The alerting configuration for one audience: which alert types notify (with
// per-type pause) and the destinations they go to. Rendered on the Settings
// page for a person's own alerts and on the Admin page for the system's; the
// Alerts page itself only shows what is happening.

const PAUSE_OPTIONS = [
  { value: "1h", label: "1 hour" },
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "", label: "Until resumed" },
];

// The shared modal chrome (same shape as Connections' dialogs).
// With a footer, only the body scrolls: the title and the buttons stay put.
const Modal = ({ title, help, wide, footer, onClose, children }) => {
  useEscape(onClose);
  const titleId = useId();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby={titleId} onMouseDown={onClose}>
      <div className={"card modal modal-sheet" + (wide ? " modal-wider" : "") + (footer ? " modal-split" : "")} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <h2 id={titleId}>{title}</h2>
        {help && <p className="hint" style={{ marginBottom: "5px" }}>{help}</p>}
        {footer ? <div className="modal-body">{children}</div> : children}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

const SeverityIcon = ({ kind }) => {
  if (kind === "critical") {
    return (
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="8" cy="8" r="6.2" />
        <path d="M8 4.8v3.8M8 11.2h.01" />
      </svg>
    );
  }
  if (kind === "warning") {
    return (
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 2.2 14.5 13.3H1.5L8 2.2Z" />
        <path d="M8 6.4v3M8 11.4h.01" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 7.2v4M8 4.8h.01" />
    </svg>
  );
};

const SeverityBadge = ({ severity }) => {
  const info = severityInfo(severity);
  return (
    <span className={info.className}>
      <SeverityIcon kind={info.icon} />
      {info.label}
    </span>
  );
};

const ProviderIcon = ({ provider }) => <img className="alert-provider-icon" src={`/api/icons/${provider === "email" ? "mail" : provider}`} alt="" aria-hidden="true" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />;

// Menu icons, drawn like the chat menu's: a paper plane (send test), a pencil
// (edit), a bin (remove), pause bars and a play triangle (resume).
const mi = (paths) => () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const TestMenuIcon = mi(<><path d="M14 2 7 9" /><path d="M14 2 9.5 14 7 9 2 6.5z" /></>);
const EditMenuIcon = mi(<path d="M11.3 2.2l2.5 2.5L6 12.5l-3.2.7.7-3.2z" />);
const RemoveMenuIcon = mi(<><path d="M3 4.5h10" /><path d="M6.5 4.5V3h3v1.5" /><path d="M4.5 4.5l.6 8.5a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8.5" /></>);
const PauseMenuIcon = mi(<><path d="M5.5 3.5v9" /><path d="M10.5 3.5v9" /></>);
const ResumeMenuIcon = mi(<path d="M5 3.2v9.6L12.5 8z" />);

// RowMenu is a row's three-dot menu, the same one the SSH keys and connections
// use; items is [{ label, icon, onClick, danger, disabled }], falsy entries skipped.
const RowMenu = ({ label, items }) => {
  const { open, setOpen, ref } = useDropdown();
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div className="menu conn-rowmenu" ref={ref}>
      <button type="button" className="btn btn-icon conn-kebab" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for ${label}`}>
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="3.1" r="1.35" />
          <circle cx="8" cy="8" r="1.35" />
          <circle cx="8" cy="12.9" r="1.35" />
        </svg>
      </button>
      {open && (
        <div className="menu-items" role="menu">
          {shown.map((it) => (
            <button key={it.label} type="button" role="menuitem" className={it.danger ? "menu-item-danger" : undefined} disabled={it.disabled} onClick={() => { setOpen(false); it.onClick(); }}>
              <span className="menu-ico" aria-hidden="true">{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Alert types ------------------------------------------------------------

const PauseDialog = ({ type, scope, onClose, onDone }) => {
  const [duration, setDuration] = useState("24h");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const who = scope === "system" ? "all system destinations" : "your destinations";
  const chosen = PAUSE_OPTIONS.find((o) => o.value === duration);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.put(`/api/alert-types/${encodeURIComponent(type.key)}/pause`, { scope, duration });
      onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Modal title={`Pause ${type.label}`} onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <p className="hint">
          Pause <b>{type.label}</b> notifications for <b>{who}</b> {chosen.value ? `for ${chosen.label.toLowerCase()}` : "until you resume them"}. Detection continues and open alerts stay in the inbox; only the notifications are held. When the pause ends, anything still open is announced once.
        </p>
        <label className="settings-field">
          <span>Duration</span>
          <select className="settings-input" value={duration} onChange={(e) => setDuration(e.target.value)} disabled={busy}>
            {PAUSE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Pausing..." : "Pause notifications"}</button>
        </div>
      </form>
    </Modal>
  );
};

// AlertTypes is the matrix: one row per alert type, its pause state, and a
// column per destination saying whether that destination receives the type.
const AlertTypes = ({ types, destinations, scope, canAct, onChanged, onRouted, setError }) => {
  const [pausing, setPausing] = useState(null);
  const [resuming, setResuming] = useState(null);
  const [busy, setBusy] = useState(false);
  // A flip shows at once and stays until the reloaded destinations arrive;
  // disabling the switch meanwhile made the cursor and opacity flicker.
  const [pending, setPending] = useState({});
  useEffect(() => setPending({}), [destinations]);
  const route = async (d, typeKey, receives) => {
    const key = d.id + "/" + typeKey;
    // Fold in this destination's other unsaved flips, so quick clicks do not undo each other
    let muted = d.muted_types || [];
    for (const [k, v] of Object.entries(pending)) {
      if (k.startsWith(d.id + "/")) muted = withReceives(muted, k.slice(d.id.length + 1), v);
    }
    muted = withReceives(muted, typeKey, receives);
    setPending((p) => ({ ...p, [key]: receives }));
    setError("");
    try {
      await api.patch(`/api/alert-destinations/${encodeURIComponent(d.id)}`, { muted_types: muted });
      onRouted();
    } catch (err) {
      setPending((p) => {
        const rest = { ...p };
        delete rest[key];
        return rest;
      });
      setError(err.message);
    }
  };
  if (types === null) return <Skeleton rows={3} label="Loading alert types..." />;
  if (types.length === 0) return null;
  const resume = async (t) => {
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/alert-types/${encodeURIComponent(t.key)}/pause?scope=${scope}`);
      setResuming(null);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="alert-section">
      <h3 className="alert-section-title">Alert types</h3>
      <p className="hint">
        What hostit watches for {scope === "system" ? "the instance" : "your apps"}, and which destination hears about each. Pausing a type holds its notifications; detection and the inbox continue.
      </p>
      <div className="table-wrap">
        <table className="alert-types">
          <thead>
            <tr>
              <th>Type</th>
              <th>Notifications</th>
              {destinations.map((d) => (
                <th key={d.id} className={"alert-route-head" + (d.enabled ? "" : " alert-route-off")} title={d.enabled ? destinationSummary(d) : `${d.label} is switched off`}>
                  <span className="alert-route-label">{d.label}</span>
                </th>
              ))}
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.key}>
                <td>
                  <div className="alert-type-label">{t.label}</div>
                  <div className="cell-muted alert-type-desc">{t.description}</div>
                </td>
                <td>
                  {t.paused ? (
                    <span className="badge badge-warn" title={t.paused_by ? `Paused by ${t.paused_by}` : undefined}>{pauseSummary(t)}</span>
                  ) : (
                    <span className="badge alert-badge-good">Enabled</span>
                  )}
                </td>
                {destinations.map((d) => {
                  const receives = pending[d.id + "/" + t.key] ?? !(d.muted_types || []).includes(t.key);
                  const provisioned = d.source === "config";
                  // A switched-off destination gets nothing; its column is locked
                  // but keeps its routing for when it is switched back on
                  const title = !d.enabled ? `${d.label} is switched off, so it gets no alerts` : provisioned ? "Provisioned in control.yml: receives every type" : undefined;
                  return (
                    <td key={d.id} className={"alert-route-cell" + (d.enabled ? "" : " alert-route-off")}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={receives}
                        className={"conn-switch" + (receives ? " on" : "")}
                        disabled={!canAct || provisioned || !d.enabled}
                        onClick={() => route(d, t.key, !receives)}
                        aria-label={`${d.label} receives ${t.label}`}
                        title={title}
                      >
                        <span className="conn-switch-knob" />
                      </button>
                    </td>
                  );
                })}
                <td className="cell-actions">
                  {canAct && <RowMenu label={t.label} items={[t.paused ? { label: "Resume alert type", icon: <ResumeMenuIcon />, onClick: () => setResuming(t) } : { label: "Pause alert type", icon: <PauseMenuIcon />, onClick: () => setPausing(t) }]} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pausing && <PauseDialog type={pausing} scope={scope} onClose={() => setPausing(null)} onDone={() => { setPausing(null); onChanged(); }} />}
      {resuming && (
        <ConfirmDialog
          title={`Resume notifications for ${resuming.label}?`}
          body="Every alert of this type that is still open is announced once, then notifications flow again."
          confirmLabel="Resume"
          danger={false}
          busy={busy}
          onClose={() => setResuming(null)}
          onConfirm={() => resume(resuming)}
        />
      )}
    </section>
  );
};

// ---- Destinations -----------------------------------------------------------

// The confirmation step of a personal Slack destination with another email:
// the bot DMs a code there, and nothing is sent until it comes back here.
const VerifyStep = ({ destination, onCancel, onVerified, onGone }) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const code = verificationCode(input);
  const email = (destination.config && destination.config.email) || "your Slack account";
  const submit = async (e) => {
    e.preventDefault();
    if (busy || !code) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/alert-destinations/${encodeURIComponent(destination.id)}/verify`, { code });
      onVerified();
    } catch (err) {
      // Too many wrong codes: the server deleted the destination
      if (err.status === 404) {
        onGone(err.message);
        return;
      }
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit}>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      <p className="hint">
        Enter the code the hostit bot sent to <b>{email}</b> in Slack. Alerts start once it is confirmed.
      </p>
      <label className="conn-field">
        <span>Code</span>
        <input type="text" inputMode="numeric" autoComplete="one-time-code" value={input} onChange={(e) => setInput(e.target.value)} placeholder="123456" maxLength={9} disabled={busy} autoFocus />
      </label>
      <div className="btn-row">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>Later</button>
        <button type="submit" className="btn btn-primary" disabled={busy || !code}>{busy ? "Confirming..." : "Confirm"}</button>
      </div>
    </form>
  );
};

const DestinationDialog = ({ scope, providers, existing, verifying, accountEmail, onClose, onSaved, onCreated, onGone }) => {
  // A destination awaiting its Slack code: set after adding one, or passed in
  // by the table's "Enter code" button
  const [pending, setPending] = useState(verifying || null);
  if (pending) {
    return (
      <Modal title={`Confirm ${pending.label}`} onClose={onClose}>
        <VerifyStep destination={pending} onCancel={onClose} onVerified={onSaved} onGone={onGone} />
      </Modal>
    );
  }
  return <DestinationForm scope={scope} providers={providers} existing={existing} accountEmail={accountEmail} onClose={onClose} onSaved={onSaved} onPending={(d) => { setPending(d); onCreated(); }} />;
};

const DestinationForm = ({ scope, providers, existing, accountEmail, onClose, onSaved, onPending }) => {
  const editing = !!existing;
  const available = PROVIDERS.filter((p) => providers.includes(p.value));
  const [provider, setProvider] = useState(existing ? existing.provider : (available[0] || {}).value || "");
  const [label, setLabel] = useState(existing ? existing.label : "");
  const [config, setConfig] = useState(existing ? { ...existing.config } : {});
  const [secret, setSecret] = useState("");
  const [clearSecret, setClearSecret] = useState(false);
  const [minSeverity, setMinSeverity] = useState(existing ? existing.min_severity : "warning");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const personal = scope !== "system";
  const base = PROVIDERS.find((p) => p.value === provider);
  // A person's Slack destination is a DM: its own fields and explanation
  const spec = base && personal && base.personalFields ? { ...base, fields: base.personalFields, help: base.personalHelp } : base;
  const hasSecret = editing && existing.has_secret && !clearSecret;
  const problem = validateDestination({ provider, label, config, secret, hasSecret, personal });
  const submit = async (e) => {
    e.preventDefault();
    if (busy || problem) return;
    setBusy(true);
    setError("");
    try {
      let saved;
      if (editing) {
        const body = { label, config, min_severity: minSeverity };
        if (clearSecret) body.secret = "";
        else if (secret) body.secret = secret;
        saved = await api.patch(`/api/alert-destinations/${encodeURIComponent(existing.id)}`, body);
      } else {
        saved = await api.post("/api/alert-destinations", { scope, provider, label, config, secret, min_severity: minSeverity });
      }
      // A Slack DM to another email waits for the code the bot sent there
      if (saved && saved.pending_verification) {
        onPending(saved);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Modal title={editing ? `Edit ${existing.label}` : "Add destination"} onClose={onClose} help={editing ? undefined : `Where ${scope === "system" ? "system" : "your"} alerts are sent.`}>
      <form onSubmit={submit}>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        {!editing && (
          <div className="alert-provider-pick" role="radiogroup" aria-label="Provider">
            {available.map((p) => (
              <button key={p.value} type="button" role="radio" aria-checked={provider === p.value} className={"alert-provider-opt" + (provider === p.value ? " on" : "")} onClick={() => { setProvider(p.value); setConfig({}); setSecret(""); }} disabled={busy}>
                <ProviderIcon provider={p.value} />
                {p.label}
              </button>
            ))}
          </div>
        )}
        {spec && <p className="hint">{spec.help}</p>}
        <label className="conn-field">
          <span>Name</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={spec ? `My ${spec.label}` : ""} maxLength={60} disabled={busy} autoFocus />
        </label>
        {spec && spec.fields.map((f) => (
          <label key={f.name} className="conn-field">
            <span>{f.label}{f.optional ? " (optional)" : ""}</span>
            <input type="text" value={config[f.name] || ""} onChange={(e) => setConfig({ ...config, [f.name]: e.target.value })} placeholder={f.placeholder || (f.name === "email" ? accountEmail : "")} disabled={busy} />
          </label>
        ))}
        {spec && spec.secret && (
          <label className="conn-field">
            <span>
              {spec.secret.label}
              {spec.secret.optional ? " (optional)" : ""}
              {hasSecret && <span className="cell-muted"> &middot; one is stored; leave empty to keep it</span>}
            </span>
            <input type="password" value={secret} onChange={(e) => { setSecret(e.target.value); setClearSecret(false); }} placeholder={hasSecret ? "(unchanged)" : spec.secret.placeholder} disabled={busy} autoComplete="new-password" />
          </label>
        )}
        {spec && spec.secret && editing && existing.has_secret && spec.secret.optional && (
          <label className="alert-filter-check">
            <input type="checkbox" checked={clearSecret} onChange={(e) => setClearSecret(e.target.checked)} disabled={busy} /> Remove the stored token
          </label>
        )}
        <label className="conn-field">
          <span>Notify from</span>
          <select className="settings-input" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)} disabled={busy}>
            {SEVERITIES.map((s) => (
              <option key={s.value} value={s.value}>{s.label} and up</option>
            ))}
          </select>
        </label>
        {problem && label && <p className="hint hint-warn">{problem}</p>}
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !!problem}>{busy ? "Saving..." : editing ? "Save" : "Add destination"}</button>
        </div>
      </form>
    </Modal>
  );
};

// The card header holds the Add button, so adding and the loaded providers
// are shared with AlertNotifications.
const Destinations = ({ scope, canAct, accountEmail, setError, adding, setAdding, onLoaded, version }) => {
  const [page, setPage] = useState(null);
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [removing, setRemoving] = useState(null);
  // Ids with a test in flight; a Set so two concurrent tests do not clobber
  const [testing, setTesting] = useState(() => new Set());
  const [tested, setTested] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const loaded = await api.get(`/api/alert-destinations?scope=${scope}`);
      setPage(loaded);
      onLoaded(loaded);
    } catch (err) {
      if (!isNetworkError(err)) setError(err.message);
    }
  }, [scope, setError, onLoaded]);
  useEffect(() => {
    load();
  }, [load, version]);
  const toggle = async (d) => {
    setError("");
    try {
      await api.patch(`/api/alert-destinations/${encodeURIComponent(d.id)}`, { enabled: !d.enabled });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const test = async (d) => {
    setTesting((s) => new Set(s).add(d.id));
    setError("");
    try {
      await api.post(`/api/alert-destinations/${encodeURIComponent(d.id)}/test`);
      setTested((t) => ({ ...t, [d.id]: "Accepted by the provider. Check that it arrived." }));
    } catch (err) {
      setTested((t) => ({ ...t, [d.id]: err.message }));
    } finally {
      setTesting((s) => {
        const next = new Set(s);
        next.delete(d.id);
        return next;
      });
      await load();
    }
  };
  // Too many wrong codes deleted the destination: say why, and show the list without it
  const gone = (message) => {
    setAdding(false);
    setVerifying(null);
    setError(message);
    load();
  };
  const remove = async (d) => {
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/alert-destinations/${encodeURIComponent(d.id)}`);
      setRemoving(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (page === null) return <Skeleton rows={2} label="Loading destinations..." />;
  const { destinations, providers } = page;
  return (
    <section className="alert-section">
      <h3 className="alert-section-title">Destinations</h3>
      <p className="hint">
        Where {scope === "system" ? "system alerts reach the admins" : "your alerts reach you"}. A destination hears about an alert when it opens or escalates, and again when it resolves.
      </p>
      {destinations.length === 0 && (
        <div className="alert-empty">
          <p className="alert-empty-title">No destinations yet.</p>
          <p className="hint">Alerts still show up in the inbox. Add a destination to be told about them on your phone, in Slack or by email.</p>
        </div>
      )}
      {destinations.length > 0 && (
        <div className="table-wrap">
          <table className="alert-dests">
            <thead>
              <tr>
                <th>Destination</th>
                <th>Notify from</th>
                <th>Last delivery</th>
                <th>Enabled</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {destinations.map((d) => (
                <tr key={d.id} className={d.enabled ? "" : "alert-dest-off"}>
                  <td>
                    <div className="alert-dest-name">
                      <ProviderIcon provider={d.provider} />
                      <b>{d.label}</b>
                      {d.source === "config" && <span className="badge" title="Defined in control.yml; edit it there">Provisioned</span>}
                      {d.source === "default" && <span className="badge" title="Added for everyone on this instance; yours to change or remove">Default</span>}
                    </div>
                    <div className="cell-muted">{destinationSummary(d)}{d.has_secret ? " \u00b7 token stored" : ""}</div>
                  </td>
                  <td>{severityInfo(d.min_severity).label} and up</td>
                  <td>
                    {(() => {
                      const st = deliveryStatus(d);
                      const cls = st.tone === "bad" ? "badge badge-danger" : st.tone === "good" ? "badge alert-badge-good" : st.tone === "pending" ? "badge badge-warn" : "badge";
                      return (
                        <span className={cls} title={d.pending_verification ? "Nothing is sent until the code the bot sent in Slack is entered" : d.last_sent_at ? `Last delivered ${new Date(d.last_sent_at).toLocaleString()}` : undefined}>
                          {st.label}
                        </span>
                      );
                    })()}
                    {tested[d.id] && <div className="cell-muted alert-dest-tested">{tested[d.id]}</div>}
                    {!tested[d.id] && deliveryStatus(d).detail && <div className="cell-muted alert-dest-tested">{deliveryStatus(d).detail}</div>}
                  </td>
                  <td>
                    <button type="button" role="switch" aria-checked={d.enabled} className={"conn-switch" + (d.enabled ? " on" : "")} onClick={() => toggle(d)} aria-label={`${d.label} enabled`} disabled={!canAct || d.source === "config"}>
                      <span className="conn-switch-knob" />
                    </button>
                  </td>
                  <td className="cell-actions">
                    <div className="alert-dest-actions">
                      {canAct && d.pending_verification && <button type="button" className="btn btn-small btn-primary" onClick={() => setVerifying(d)}>Enter code</button>}
                      {canAct && (
                        <RowMenu
                          label={d.label}
                          items={[
                            !d.pending_verification && { label: testing.has(d.id) ? "Sending..." : "Send test", icon: <TestMenuIcon />, onClick: () => test(d), disabled: testing.has(d.id) },
                            d.source !== "config" && { label: "Edit", icon: <EditMenuIcon />, onClick: () => setEditing(d) },
                            d.source !== "config" && { label: "Remove", icon: <RemoveMenuIcon />, onClick: () => setRemoving(d), danger: true },
                          ]}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <DestinationDialog scope={scope} providers={providers} accountEmail={accountEmail} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} onCreated={load} onGone={gone} />}
      {editing && <DestinationDialog scope={scope} providers={providers} accountEmail={accountEmail} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onCreated={load} onGone={(m) => { setEditing(null); gone(m); }} />}
      {verifying && <DestinationDialog scope={scope} providers={providers} accountEmail={accountEmail} verifying={verifying} onClose={() => setVerifying(null)} onSaved={() => { setVerifying(null); load(); }} onCreated={load} onGone={gone} />}
      {removing && (
        <ConfirmDialog
          title={`Remove "${removing.label}"?`}
          body="Nothing will be sent there any more. Notifications already sent stay in each alert's record."
          confirmLabel="Remove"
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
        />
      )}
    </section>
  );
};

// AlertNotifications is the card a settings page embeds: the types and the
// destinations for one scope, loading their own data.
export const AlertNotifications = ({ scope, canAct, accountEmail }) => {
  const [types, setTypes] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [page, setPage] = useState(null);
  const [version, setVersion] = useState(0);
  const providers = page ? page.providers : [];
  const load = useCallback(async () => {
    try {
      setTypes(await api.get(`/api/alert-types?scope=${scope}`));
    } catch (err) {
      if (!isNetworkError(err)) setError(err.message);
    }
  }, [scope]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="card">
      <div className="card-header">
        <h2>{scope === "system" ? "Alerting" : "Notifications"}</h2>
        {canAct && providers.length > 0 && (
          <button type="button" className="btn btn-small btn-primary" onClick={() => setAdding(true)}>Add destination</button>
        )}
      </div>
      <p className="hint">
        {scope === "system"
          ? "How the instance tells its admins about trouble: which checks notify, and where. Everything still shows on the Alerts page."
          : "How you are told about trouble with your apps: which checks notify you, and where. Everything still shows on the Alerts page."}{" "}
        <DocsLink guide="user" section="alerts">How alerting works</DocsLink>
      </p>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      <Destinations scope={scope} canAct={canAct} accountEmail={accountEmail} setError={setError} adding={adding} setAdding={setAdding} onLoaded={setPage} version={version} />
      <AlertTypes
        types={types}
        destinations={page ? page.destinations : []}
        scope={scope}
        canAct={canAct}
        onChanged={load}
        onRouted={() => setVersion((v) => v + 1)}
        setError={setError}
      />
    </div>
  );
};

export { Modal, PauseDialog, ProviderIcon, SeverityBadge, useEscape };
